import { Box, Divider, HStack, Stack, Text } from "@chakra-ui/react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  PersonDto,
  SeV2AttachmentDto,
  SeV2QuestionDto,
  SeV2QuestionKind,
  SeV2QuestionStatus,
} from "../../../autogen/bff-api";
import { TipTap } from "../../input/TipTap/TipTap";
import { AttachmentList } from "./AttachmentList";
import { formatDateTime, personName } from "./communicationUtils";

interface ThreadEntry {
  key: string;
  person: PersonDto;
  orgName: string;
  fromSupplier: boolean;
  at: string;
  content: string;
  attachments: SeV2AttachmentDto[];
}

const useClarificationStatusLabel = (viewerIsBuyer: boolean) => {
  const { t } = useTranslation();
  return (status: SeV2QuestionStatus): string => {
    if (status === SeV2QuestionStatus.Unanswered) {
      return viewerIsBuyer ? t("Waiting for the tenderer") : t("Awaiting your reply");
    }
    return viewerIsBuyer ? t("Reply received") : t("You have replied");
  };
};

const ClarificationStatusChip = ({
  status,
  viewerIsBuyer,
}: {
  status: SeV2QuestionStatus;
  viewerIsBuyer: boolean;
}) => {
  const label = useClarificationStatusLabel(viewerIsBuyer)(status);
  const waiting = status === SeV2QuestionStatus.Unanswered;
  return (
    <Box
      as="span"
      bg={waiting ? "smChipAmberBg" : "smChipBlueBg"}
      color={waiting ? "smChipAmberFg" : "smChipBlueFg"}
      rounded="full"
      px="2.5"
      py="0.5"
      fontSize="xs"
      fontWeight="semibold"
      flexShrink={0}
    >
      {label}
    </Box>
  );
};

export const ClarificationThread = ({
  question,
  viewerIsBuyer,
  footer,
}: {
  question: SeV2QuestionDto;
  viewerIsBuyer: boolean;
  footer?: ReactNode;
}) => {
  const { t } = useTranslation();

  const entries: ThreadEntry[] = [
    {
      key: "opening",
      person: question.askedByPerson,
      orgName: question.askedByOrgName,
      fromSupplier: question.kind === SeV2QuestionKind.Question,
      at: question.createdAt,
      content: question.content,
      attachments: question.attachments ?? [],
    },
    ...(question.messages ?? []).map((message, index) => ({
      key: `message-${index}`,
      person: message.sentBy,
      orgName: message.sentByOrgName,
      fromSupplier: message.sentByOrganizationId === question.supplierOrganizationId,
      at: message.sentAt,
      content: message.content,
      attachments: message.attachments ?? [],
    })),
  ];

  return (
    <Box borderWidth="1px" borderColor="smBorder" rounded="lg" bg="smBackground" overflow="hidden">
      <HStack px="4" py="3" justify="space-between" align="center" spacing="3">
        <Stack spacing="0.5" minW={0}>
          <Text fontWeight="semibold" color="smText" noOfLines={1}>
            {question.subject}
          </Text>
          <Text fontSize="xs" color="smMuted">
            {t("Clarification")} · {viewerIsBuyer ? question.supplierOrgName : question.askedByOrgName}
          </Text>
        </Stack>
        <ClarificationStatusChip status={question.status} viewerIsBuyer={viewerIsBuyer} />
      </HStack>
      <Divider borderColor="smBorder" />

      <Stack spacing="3" px="4" py="4">
        {entries.map((entry) => (
          <Box
            key={entry.key}
            borderWidth="1px"
            borderColor="smBorder"
            bg={entry.fromSupplier ? "smBackground" : "smPrimarySofter"}
            rounded="md"
            p="3"
          >
            <Text fontSize="xs" color="smMuted" mb="1">
              {personName(entry.person)} · {entry.orgName} · {formatDateTime(entry.at)}
            </Text>
            <TipTap content={entry.content} editable={false} maxHeight="96" />
            <AttachmentList eventId={question.eventId} attachments={entry.attachments} />
          </Box>
        ))}
        {footer}
      </Stack>
    </Box>
  );
};
